import React, { createContext, useContext, useEffect, useState } from "react";
import { Layout, Row, Col } from "antd";
import Sidebar from "../Sidebar/index";
import Topbar from "../Topbar/index";
import AdminRoutes from "routes/index";
import BadResponse from "../BadResponse";
import { get } from "lodash";

import {
   NAV_STYLE_ABOVE_HEADER,
   NAV_STYLE_BELOW_HEADER,
   NAV_STYLE_DARK_HORIZONTAL,
   NAV_STYLE_DEFAULT_HORIZONTAL,
   NAV_STYLE_DRAWER,
   NAV_STYLE_FIXED,
   NAV_STYLE_INSIDE_HEADER_HORIZONTAL,
   NAV_STYLE_MINI_SIDEBAR,
} from "../../constants/ThemeSetting";

import CircularProgress from "../../components/CircularProgress";
import { layoutSettingsVar } from "../../apollo/local/cache";
import { useQuery, useReactiveVar } from "@apollo/client";
import { GQL_INIT_QUERY } from "../../apollo/query/user";
import { AbilityContext, defineRulesFor, isAdmin } from "../../acl/index";
import { injectIntl } from "react-intl";
import { useLocation, useHistory } from "react-router-dom";
import { getAdminPath } from "../../util/router";
import Notification from "../Notification";
import { useIsMobileVersion } from "../../hooks/useIsMobileVersion";
import useIdleTimeout from "../../hooks/useIdleTimeout";
import { localStorageClass } from "../../apollo/local/storage";

export const AdminContext = createContext();

const ProtectedMainApp = ({ user }) => {
   const [selectedDossierIds, setSelectedDossierIds] = useState([]);
   const workspaces = get(user, "current_organization.workspaces", []);

   const ability = useContext(AbilityContext);
   const { navStyle } = useReactiveVar(layoutSettingsVar);
   const location = useLocation();
   const history = useHistory();
   const isMobileVersion = useIsMobileVersion();

   const timeoutMs =
      process.env.REACT_APP_IDLE_TIMEOUT_SECONDS &&
      !isNaN(process.env.REACT_APP_IDLE_TIMEOUT_SECONDS)
         ? parseInt(process.env.REACT_APP_IDLE_TIMEOUT_SECONDS, 10) * 1000
         : null;

   useIdleTimeout(() => {
      // Esegui il logout dell'utente
      localStorageClass.logout();
      history.push("/");
   }, timeoutMs);

   useEffect(() => {
      if (user) {
         const rules = defineRulesFor(user);
         ability.update(rules);
      }
   }, [user, ability]);

   useEffect(() => {
      if (location.pathname === getAdminPath() && user) {
         if (isAdmin(user)) {
            history.replace(getAdminPath("dashboard"));
         } else {
            history.replace(getAdminPath("workarea"));
         }
      }
   }, [location, user]);

   const getContainerClass = (navStyle) => {
      switch (navStyle) {
         case NAV_STYLE_DARK_HORIZONTAL:
            return "gx-container-wrap";
         case NAV_STYLE_DEFAULT_HORIZONTAL:
            return "gx-container-wrap";
         case NAV_STYLE_INSIDE_HEADER_HORIZONTAL:
            return "gx-container-wrap";
         case NAV_STYLE_BELOW_HEADER:
            return "gx-container-wrap";
         case NAV_STYLE_ABOVE_HEADER:
            return "gx-container-wrap";
         default:
            return "";
      }
   };
   const getNavStyles = (navStyle, user) => {
      switch (navStyle) {
         case NAV_STYLE_FIXED:
         case NAV_STYLE_DRAWER:
         case NAV_STYLE_MINI_SIDEBAR:
            return <Topbar user={user} />;
         default:
            return null;
      }
   };

   return (
      <AdminContext.Provider value={{ selectedDossierIds, setSelectedDossierIds }}>
         <Layout className="gx-app-layout">
            {isMobileVersion ? null : (
               <Sidebar user={user} workspaces={workspaces} />
            )}
            <Layout>
               {getNavStyles(navStyle, user)}
               <Layout.Content
                  className={`gx-layout-content ${getContainerClass(navStyle)} `}
               >
                  <AdminRoutes ability={ability} user={user} />
                  <Layout.Footer>
                     <Row>
                        <Col sm={24}>
                           <div className="gx-layout-footer-content gx-text-right">
                              <a
                                 href={process.env.REACT_APP_CREDITS_URL}
                                 target="_bweb"
                                 className="gx-text-grey"
                              >
                                 <small>{process.env.REACT_APP_CREDITS}</small>
                              </a>

                              {process.env.REACT_APP_GIT_VERSION && (
                                 <small className="gx-ml-1 gx-text-grey">
                                    v
                                    {process.env.REACT_APP_GIT_VERSION.substring(
                                       0,
                                       7
                                    )}
                                 </small>
                              )}
                           </div>
                        </Col>
                     </Row>
                     <Notification />
                  </Layout.Footer>
               </Layout.Content>
            </Layout>
         </Layout>
      </AdminContext.Provider>
   );
};

const BLOCKED_SESSION_CATEGORIES = ["no-active-clients", "no-active-contract"];

const MainApp = ({ intl }) => {
   const {
      loading: loadingProfile,
      error: errorProfile,
      data,
   } = useQuery(GQL_INIT_QUERY);

   const category = get(
      errorProfile,
      "graphQLErrors[0].extensions.category",
      ""
   );
   const isBlockedSession = BLOCKED_SESSION_CATEGORIES.includes(category);

   useEffect(() => {
      // La sessione ha ancora un token valido ma l'utente non può usare l'app:
      // sloggarlo subito evita che il pulsante "Home" lo rimandi qui in loop.
      if (isBlockedSession) {
         localStorageClass.logout();
      }
   }, [isBlockedSession]);

   if (loadingProfile) {
      return (
         <div className="gx-loader-view">
            <CircularProgress />
         </div>
      );
   }

   if (errorProfile) {
      if (category === "no-active-clients") {
         return (
            <BadResponse
               status="403"
               title={intl.formatMessage({ id: "workarea.no_customer_allowed" })}
               subtitle={intl.formatMessage({
                  id: "workarea.no_customer_allowed_text",
               })}
            />
         );
      }

      if (category === "no-active-contract") {
         return (
            <BadResponse
               status="403"
               title={intl.formatMessage({ id: "workarea.no_active_contract" })}
               subtitle={intl.formatMessage({
                  id: "workarea.no_active_contract_text",
               })}
            />
         );
      }

      return (
         <BadResponse
            title={intl.formatMessage({ id: "500.ooops" })}
            subtitle={intl.formatMessage({ id: "500.something_wrong" })}
         />
      );
   }

   return <ProtectedMainApp user={data.me} />;
};

export default injectIntl(MainApp);
